import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
const we_next_logo = '/assets/wenext_logo_dark.png';

export function LandingTermsOfService({ hideHeader = false }: { hideHeader?: boolean }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      {!hideHeader && (
        <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <img
                  src={we_next_logo}
                  alt="WeNext Logo"
                  className="w-[200px] min-w-[200px] cursor-pointer"
                  onClick={() => navigate('/')}
                />
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </nav>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center">Terms of Service</CardTitle>
            <p className="text-center text-muted-foreground">Effective Date: October 2025</p>
            <p className="text-center text-muted-foreground">Entity: PhotonX Technologies Private Limited</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. ABOUT WENEXT.AI</h2>
              <p>
                WeNext.ai is a WhatsApp automation and CRM platform offering businesses and individual users tools for:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>WhatsApp automation</li>
                <li>Customer relationship management (CRM)</li>
                <li>AI-driven messaging, analytics, and workflows</li>
                <li>Reporting, dashboards, and performance insights</li>
                <li>Integrations with third-party platforms including WhatsApp APIs and other tools</li>
              </ul>
              <p className="mt-2">
                The Services are offered on a hybrid model, including free features and paid subscription plans.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. ELIGIBILITY & ACCOUNT REGISTRATION</h2>
              <p>2.1 You must be at least 18 years of age to use the Services.</p>
              <p>2.2 You agree to provide accurate, complete, and current information during registration and keep your account details updated.</p>
              <p>2.3 Accounts may have multiple roles, including Admin and User roles. Admin users are responsible for all activities performed under their account.</p>
              <p>2.4 You are responsible for maintaining the confidentiality of your login credentials. WeNext.ai is not liable for unauthorized access caused by your failure to secure your account.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. ACCEPTABLE USE</h2>
              <p>You agree not to use the Services to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Violate any applicable law or regulation</li>
                <li>Send spam, phishing messages, or unsolicited communications</li>
                <li>Infringe intellectual property or privacy rights</li>
                <li>Transmit malicious code, malware, or harmful data</li>
                <li>Abuse WhatsApp APIs or violate WhatsApp’s own terms</li>
                <li>Engage in deceptive, fraudulent, or illegal activities</li>
              </ul>
              <p className="mt-2">We reserve the right to suspend or terminate accounts that violate these Terms without prior notice.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. SUBSCRIPTIONS, BILLING & PAYMENTS</h2>
              <p><strong>4.1 Pricing & Plans:</strong> Pricing is fixed and published on the WeNext.ai website. No custom or negotiable pricing applies unless explicitly agreed in writing.</p>
              <p><strong>4.2 Billing Cycle:</strong> Paid subscriptions are billed on a monthly or annual recurring basis, depending on the plan selected.</p>
              <p><strong>4.3 Auto-Renewal:</strong> All paid plans automatically renew unless cancelled before the renewal date.</p>
              <p><strong>4.4 Payment Method:</strong> Payments are accepted via credit and debit cards through third-party payment processors.</p>
              <p><strong>4.5 No Refund Policy:</strong> All payments are non-refundable. No refunds or credits will be provided for partial usage, unused time, downgrades, or cancellations. This is non-negotiable.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. CANCELLATION & TERMINATION</h2>
              <p>5.1 You may cancel your subscription at any time. Cancellation stops future billing but does not entitle you to a refund.</p>
              <p>5.2 We reserve the right to suspend or terminate access to the Services immediately if:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>You breach these Terms</li>
                <li>Your usage creates legal, technical, or reputational risk</li>
                <li>Required by law or regulatory authority</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. DATA, PRIVACY & AI USAGE</h2>
              <p><strong>6.1 Data Collected:</strong> We collect:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Personal information (name, email, phone number)</li>
                <li>Account, usage, and behavioural data</li>
              </ul>
              <p className="mt-2"><strong>6.2 AI & Model Improvement:</strong> You acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Data processed through the platform may be used to improve AI models, automation accuracy, and platform performance</li>
                <li>We do not sell user data to third parties</li>
              </ul>
              <p className="mt-2"><strong>6.3 Third-Party Integrations:</strong> Use of third-party services (e.g., WhatsApp APIs) is subject to their respective terms. WeNext.ai is not responsible for third-party service disruptions or policy changes.</p>
              <p><strong>6.4 Data Security:</strong> We implement reasonable technical and organizational measures to protect user data. However, no system is 100% secure.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. INTELLECTUAL PROPERTY</h2>
              <p>7.1 All intellectual property related to the platform, including software, designs, logos, workflows, and AI systems, belongs to PhotonX Technologies.</p>
              <p>7.2 You are granted a limited, non-exclusive, non-transferable license to use the Services during your subscription term.</p>
              <p>7.3 You retain ownership of your business data and content uploaded to the platform.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. SERVICE AVAILABILITY & MODIFICATIONS</h2>
              <p>8.1 We do not guarantee uninterrupted or error-free service.</p>
              <p>8.2 We reserve the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Modify features</li>
                <li>Add or remove functionalities</li>
                <li>Discontinue parts of the Services</li>
              </ul>
              <p className="mt-2">without liability, provided core paid functionality is reasonably maintained.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. DISCLAIMERS</h2>
              <p>The Services are provided on an “as is” and “as available” basis.</p>
              <p>We expressly disclaim warranties of:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fitness for a particular purpose</li>
                <li>Guaranteed delivery, conversions, or business outcomes</li>
                <li>Compliance with third-party platform policies (e.g., WhatsApp)</li>
              </ul>
              <p className="mt-2">You use the platform at your own risk.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. LIMITATION OF LIABILITY</h2>
              <p>To the maximum extent permitted by law:</p>
              <p>PhotonX Technologies shall not be liable for:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Loss of profits, data, reputation, or business</li>
                <li>Service interruptions or third-party failures</li>
              </ul>
              <p className="mt-2">Our total liability shall not exceed the amount paid by you in the last 3 months preceding the claim.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. INDEMNIFICATION</h2>
              <p>You agree to indemnify and hold harmless PhotonX Technologies against any claims, damages, or losses arising from:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your use of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your misuse of WhatsApp or other third-party platforms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. GOVERNING LAW & JURISDICTION</h2>
              <p>These Terms are governed by the laws of India.</p>
              <p>Any disputes shall be subject to the exclusive jurisdiction of courts in Hyderabad, Telangana.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">13. CHANGES TO TERMS</h2>
              <p>We may update these Terms from time to time. Continued use of the Services after changes constitutes acceptance of the revised Terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">14. CONTACT INFORMATION</h2>
              <p>For legal, billing, or support queries:</p>
              <div className="bg-muted p-4 rounded-lg mt-2 space-y-1">
                <p className="font-semibold">PhotonX Technologies</p>
                <p>Hyderabad, Telangana, India</p>
                <p>📧 <a href="mailto:hello@wenext.ai" className="text-primary hover:underline">hello@wenext.ai</a></p>
                <p>🌐 <a href="https://wenext.ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://wenext.ai</a></p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
